import React from 'react';
import { useSelector } from 'react-redux';
import { Mail, Phone, Globe, Calendar, User, MapPin, Shield } from 'lucide-react';

const UserDetailsView = () => {
    const { userList: user } = useSelector((state) => state.users);

    if (!user) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
                <p className="text-secondary fs-5">Loading user details...</p>
            </div>
        );
    }
    
    const userData = user;

    const formatDate = (date) => new Date(date).toLocaleDateString('en-US', {
        year: 'numeric', month: 'short', day: 'numeric'
    });

    return (
        <div className="card shadow-sm mb-4 mt-3">
            {/* Header */}
            <div className="card-header bg-primary text-white rounded-top p-4">
                <div className="row align-items-center">
                    {userData.image ? (
                        <div className="col-auto">
                            <img
                                src={userData.image}
                                alt={userData.full_name}
                                className="rounded-circle"
                                style={{ width: '100px', height: '100px', objectFit: 'cover' }}
                            />
                        </div>
                    ) : (
                        <div className="col-auto">
                            <div className="bg-light rounded-circle d-flex justify-content-center align-items-center" 
                                 style={{ width: '100px', height: '100px' }}>
                                <span className="fs-1 text-primary fw-bold">{userData.full_name?.charAt(0)}</span>
                            </div>
                        </div>
                    )}
                    <div className="col">
                        <h2 className="fw-bold mb-1 text-white">{userData.full_name}</h2>
                        <div className="d-flex align-items-center">
                            <span className="badge bg-success me-2">Active User</span>
                            {/* <span className="fw-semibold">
                                Joined {formatDate(userData.createdAt)}
                            </span> */}
                        </div>
                    </div>
                </div>
            </div>

            {/* User Details */}
            <div className="card-body">
                <div className="row mb-4">
                    <div className="col-lg-8">
                        <h5 className="fw-semibold border-bottom pb-2 mb-3">👤 User Profile</h5>
                        <div className="row mb-3">
                            {userData.email && (
                                <div className="col-sm-6 mb-2 d-flex align-items-start">
                                    <Mail className="me-2 text-primary" />
                                    <div>
                                        <small className="text-muted">Email</small><br />
                                        <strong>{userData.email || "Not specified"}</strong>
                                    </div>
                                </div>
                            )}
                            <div className="col-sm-6 mb-2 d-flex align-items-start">
                                <Phone className="me-2 text-primary" />
                                <div>
                                    <small className="text-muted">Phone</small><br />
                                    <strong>+{userData.country_code} {userData.phone_number}</strong>
                                </div>
                            </div>
                            <div className="col-sm-6 mb-2 d-flex align-items-start">
                                <User className="me-2 text-primary" />
                                <div>
                                    <small className="text-muted">Date of Birth</small><br />
                                    <strong>{userData.dob ? formatDate(userData.dob) : "Not specified"}</strong>
                                </div>
                            </div>
                            {userData.organization && (
                                <div className="col-sm-6 mb-2 d-flex align-items-start">
                                    <Globe className="me-2 text-primary" />
                                    <div>
                                        <small className="text-muted">Organization</small><br />
                                        <strong>{userData.organization}</strong>
                                    </div>
                                </div>
                            )}
                            <div className="col-sm-6 mb-2 d-flex align-items-start">
                                <Calendar className="me-2 text-primary" />
                                <div>
                                    <small className="text-muted">Registered On</small><br />
                                    <strong>{formatDate(userData.createdAt)}</strong>
                                </div>
                            </div>
                            {userData.user_language && (
                                <div className="col-sm-6 mb-2 d-flex align-items-start">
                                    <Globe className="me-2 text-primary" />
                                    <div>
                                        <small className="text-muted">Preferred Language</small><br />
                                        <strong>{userData.user_language.name}</strong>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Account Info */}
                        <h5 className="fw-semibold border-bottom pb-2 mb-3">🔐 Account Information</h5>
                        <div className="row mb-3">
                            <div className="col-sm-6 mb-2 d-flex align-items-start">
                                <Shield className="me-2 text-primary" />
                                <div>
                                    <small className="text-muted">Account Status</small><br />
                                    <strong>{userData.is_active ? "Active" : "Inactive"}</strong>
                                </div>
                            </div>
                            <div className="col-sm-6 mb-2 d-flex align-items-start">
                                <Calendar className="me-2 text-primary" />
                                <div>
                                    <small className="text-muted">Last Login</small><br />
                                    <strong>{userData.user_device_info?.last_login ? formatDate(userData.user_device_info.last_login) : "Not available"}</strong>
                                </div>
                            </div>
                            {/* <div className="col-sm-6 mb-2 d-flex align-items-start">
                                <Globe className="me-2 text-primary" />
                                <div>
                                    <small className="text-muted">Login Type</small><br />
                                    <strong>{userData.login_type === "S" ? "Simple" : userData.login_type}</strong>
                                </div>
                            </div> */}
                            {/* <div className="col-sm-6 mb-2 d-flex align-items-start">
                                <User className="me-2 text-primary" />
                                <div>
                                    <small className="text-muted">Role</small><br />
                                    <strong>{userData.role ? userData.role.charAt(0).toUpperCase() + userData.role.slice(1) : "User"}</strong>
                                </div>
                            </div> */}
                        </div>
                    </div>

                    {/* Location & Device Info */}
                    <div className="col-lg-4">
                        {/* <h5 className="fw-semibold border-bottom pb-2 mb-3">📱 Device Information</h5>
                        {userData.user_device_info ? (
                            <div className="card mb-3 border-0 shadow-sm">
                                <div className="card-body">
                                    <h6 className="mb-1 fw-bold">Device Details</h6>
                                    <p className="text-muted small mb-1">
                                        <strong>Device:</strong> {userData.user_device_info.device_name || "Not available"}
                                    </p>
                                    <p className="text-muted small mb-1">
                                        <strong>Type:</strong> {userData.user_device_info.device_type === "W" ? "Web" : userData.user_device_info.device_type}
                                    </p>
                                    <p className="text-muted small mb-1">
                                        <strong>OS Version:</strong> {userData.user_device_info.os_version || "Not available"}
                                    </p>
                                    <p className="text-muted small mb-1">
                                        <strong>App Version:</strong> {userData.user_device_info.app_version || "Not available"}
                                    </p>
                                </div>
                            </div>
                        ) : (
                            <p className="text-muted">No device information available.</p>
                        )} */}

                        <h5 className="fw-semibold border-bottom pb-2 mb-3 mt-4">📍 Location</h5>
                        {(userData.user_country || userData.user_city) ? (
                            <div className="card border-0 shadow-sm">
                                <div className="card-body">
                                    <div className="d-flex align-items-start mb-2">
                                        <MapPin className="text-primary me-2 mt-1" />
                                        <div>
                                            {userData.user_country && (
                                                <p className="mb-1">
                                                    <strong>Country:</strong> {userData.user_country.name}
                                                </p>
                                            )}
                                            {userData.user_city && (
                                                <p className="mb-0">
                                                    <strong>City:</strong> {userData.user_city.name}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <p className="text-muted">No location information available.</p>
                        )}

                        {/* Government Certificate */}
                        {userData.government_certificate && (
                            <>
                                <h5 className="fw-semibold border-bottom pb-2 mb-3 mt-4">📄 Documents</h5>
                                <div className="card border-0 shadow-sm">
                                    <div className="card-body">
                                        <h6 className="mb-2">Government Certificate</h6>
                                        <a href={userData.government_certificate} target="_blank" rel="noreferrer" className="btn btn-sm btn-outline-primary">
                                            View Certificate
                                        </a>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UserDetailsView;